import { useState } from 'react';
import {
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Headphones,
  AlertTriangle,
  Mail,
  MessageCircle,
  BookOpen,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

const PAGE_LABELS = {
  '/admin/tracking': 'Suivi GPS',
  '/admin/fleet': 'Flotte en temps réel',
  '/admin/students': 'Élèves',
  '/admin/parents': 'Parents',
  '/admin/drivers': 'Chauffeurs',
  '/admin/buses': 'Bus',
  '/admin/stops': 'Arrêts',
  '/admin/assignments': 'Assignations',
  '/admin/dashboard': 'Tableau de bord',
  '/admin/notifications': 'Notifications',
  '/admin/requests': 'Demandes',
  '/admin/settings': 'Paramètres',
  '/admin/faq': 'FAQ',
};
import { useAuth } from '../hooks/useAuthSimple';
import { useAdminRealtimeNotifications } from '../hooks/useAdminRealtimeNotifications';
import { useWebPushRegistration } from '../hooks/useWebPushRegistration';
import { SidebarTrigger } from './ui/sidebar';
import logger from '@/utils/logger';
import DevSimBadge from './DevSimBadge';

const IS_DEV_MODE =
  import.meta.env.VITE_DEV_MODE === 'false' ? false : import.meta.env.VITE_DEV_MODE === 'true' || import.meta.env.DEV;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const pageLabel = PAGE_LABELS[location.pathname] || null;
  const [profileOpen, setProfileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  // Vérifier si l'offre est Basic (calculé ici, avant le hook temps réel qui en a besoin)
  const subscriptionPlan = user?.school?.subscription_plan || user?.subscription_plan || 'basic';
  const isBasicPlan = subscriptionPlan?.toLowerCase() === 'basic';

  const { notificationCount, resetCount } = useAdminRealtimeNotifications({
    enabled: !isBasicPlan,
    schoolId: user?.school?.id,
    navigate,
  });
  // Le premier clic de l'admin sur n'importe quelle page déclenche la demande de
  // permission navigateur (voir useWebPushRegistration.js) — rien à câbler ici.
  useWebPushRegistration({ enabled: !isBasicPlan, navigate });

  const handleNotificationClick = () => {
    resetCount();
    navigate('/admin/notifications');
  };

  const handleSupportClick = () => {
    setSupportOpen(!supportOpen);
    setProfileOpen(false);
  };

  const handleSettingsClick = () => {
    setProfileOpen(false); // Fermer le menu
    navigate('/admin/settings');
  };

  const handleLogoutClick = () => {
    setProfileOpen(false); // Fermer le menu
    setShowLogoutConfirm(true); // Afficher la popup
  };

  const confirmLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      logger.error('Erreur déconnexion:', error);
      navigate('/login');
    }
  };

  const currentSchool = user?.school || {
    name: 'École non définie',
    code: 'N/A',
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 h-16">
      <div className="flex items-center justify-between h-full px-3 sm:px-6">
        {/* Left section */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <SidebarTrigger className="md:hidden -ml-1 text-gray-500 hover:text-gray-700 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center space-x-1.5 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{currentSchool.name}</h2>
              {pageLabel && (
                <>
                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 hidden sm:block" />
                  <span className="text-lg font-semibold text-gray-500 truncate hidden sm:block">{pageLabel}</span>
                </>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">
              {user?.role === 'superadmin' ? 'Super Admin' : 'Administrateur'}
              {currentSchool.code !== 'N/A' && ` • ${currentSchool.code}`}
            </p>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center space-x-1.5 sm:space-x-4 shrink-0">
          {IS_DEV_MODE && <DevSimBadge />}
          {/* Notifications - Caché si l'offre est Basic */}
          {!isBasicPlan && (
            <button
              onClick={handleNotificationClick}
              className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              title="Notifications"
            >
              <Bell className="w-6 h-6" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {notificationCount}
                </span>
              )}
            </button>
          )}

          {/* Contacter support */}
          <div className="relative">
            <button
              onClick={handleSupportClick}
              className={`p-2 rounded-full transition-colors ${
                supportOpen ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Support et FAQ"
            >
              <Headphones className="w-6 h-6" />
            </button>

            <AnimatePresence>
              {supportOpen && (
                <>
                  {/* Overlay pour fermer au clic extérieur */}
                  <div className="fixed inset-0 z-40" onClick={() => setSupportOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b border-gray-100 mb-1">
                      <p className="text-sm font-semibold text-gray-900">Support et FAQ</p>
                      <p className="text-xs text-gray-500">Comment pouvons-nous vous aider ?</p>
                    </div>

                    {/* Option 1 — FAQ */}
                    <a
                      href="/admin/faq"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSupportOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Accéder à la FAQ</p>
                        <p className="text-xs text-gray-500">Questions fréquentes</p>
                      </div>
                    </a>

                    {/* Option 2 — Email */}
                    <a
                      href="mailto:contact@meyebus.ma"
                      onClick={() => setSupportOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Contactez-nous par email</p>
                        <p className="text-xs text-gray-500">contact@meyebus.ma</p>
                      </div>
                    </a>

                    {/* Option 3 — WhatsApp */}
                    <a
                      href="https://wa.me/212631387567"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSupportOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Contactez-nous par WhatsApp</p>
                        <p className="text-xs text-gray-500">+212 6 31 38 75 67</p>
                      </div>
                    </a>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <span className="hidden md:block text-sm font-medium text-gray-700">
                {user?.first_name} {user?.last_name}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            <AnimatePresence>
              {profileOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 w-48 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                >
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-blue-600 font-medium">
                      {user?.role === 'superadmin' ? 'Super Admin' : 'Administrateur'}
                    </p>
                  </div>

                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Paramètres
                  </button>

                  <button
                    onClick={handleLogoutClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Déconnexion
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Popup de confirmation de déconnexion */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">Confirmer la déconnexion</h3>
                <p className="text-center text-gray-500 mb-6">
                  Êtes-vous sûr de vouloir vous déconnecter de votre session administrateur ?
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowLogoutConfirm(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmLogout}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Se déconnecter
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
